import type { ContractParticipantParty } from '@paid-ui/types/parties';

import { sum, sumBy } from 'lodash';

import { ContractRole } from '@paid-ui/enums/contract';
import { ContractPartyTag, PartyRole, PayType } from '@paid-ui/enums/party';
import { ContractAction } from '@paid-ui/enums/requiring-action';
import { SigningStatus } from '@paid-ui/enums/signatory';
import { ApprovalStatus } from '@paid-ui/enums/snapshot';
import { saveProjectUserGroupAccount } from '@paid-ui/models/project-user-group';
import {
  checkIfPartyJoined,
  getContractParticipantParties,
  getInviteeParty,
  getInviterParty,
  getLastReviewParty,
  getPartiesByRole,
  getPartyByRole,
  getPayeeParty,
  getPayerParty,
  getTheOtherClaimReviewParty,
} from '@paid-ui/utils/parties';
import { isNonContractualParty as checkIfNonContractualParty } from '@paid-ui/utils/party';

import { contractManager } from '../model';

export const saveParticipantParties = (
  originalParties: ContractParticipantParty[],
  isRBN?: boolean,
) => {
  const parties = isRBN
    ? originalParties.map(({ role, ...party }) => {
        return {
          ...party,
          role: role
            ? {
                ...role,
                actions: [...new Set([...role.actions, ContractAction.CONTRACT_SIGN])],
              }
            : undefined,
        } as ContractParticipantParty;
      })
    : originalParties;

  const inviteeParty = getInviteeParty(parties);
  const inviterParty = getInviterParty(parties);
  const payeeParty = getPayeeParty(parties);
  const payerParty = getPayerParty(parties);
  const superintendent = getPartyByRole(parties, ContractRole.SUPERINTENDENT);
  const architect = getPartyByRole(parties, ContractRole.ARCHITECT);
  const payerAgent = getPartyByRole(parties, ContractRole.PAYER_AGENT);
  const funders = getPartiesByRole(parties, ContractRole.SPONSOR);
  const qss = getPartiesByRole(parties, ContractRole.QUANTITY_SURVEYOR);
  const lastReviewParty = getLastReviewParty(parties);
  const { currentParty, theOtherParty } = getContractParticipantParties(parties);
  const theOtherClaimReviewParty = getTheOtherClaimReviewParty(parties);

  const isInvitee = currentParty?.invitationType === PartyRole.INVITEE;
  const isInviter = currentParty?.invitationType === PartyRole.INVITER;
  const isPayee = currentParty?.payType === PayType.PAYEE;
  const isPayer = currentParty?.payType === PayType.PAYER;
  const isSuperintendent = currentParty?.role?.role === ContractRole.SUPERINTENDENT;
  const isArchitect = currentParty?.role?.role === ContractRole.ARCHITECT;
  const isFunder = currentParty?.role?.role === ContractRole.SPONSOR;
  const isQS = currentParty?.role?.role === ContractRole.QUANTITY_SURVEYOR;
  const isNonContractualParty =
    currentParty?.tags?.includes(ContractPartyTag.NON_CONTRACTUAL_PARTY) ?? false;

  const isPayerHasAccount = !!payerParty?.account;
  const numberOfParticipants = parties.length;
  const numberOfSignatories = sum(
    parties.map((party) => party.usersInfo?.filter((user) => user.signingStatus).length ?? 0),
  );

  const joinedList = parties.filter(checkIfPartyJoined);
  const numberOfJoined = joinedList.length;
  const allContractualPartiesJoined = parties
    .filter((party) => !checkIfNonContractualParty(party))
    .every(checkIfPartyJoined);
  const numberOfSigned = sumBy(parties, (item) => {
    const signed = item.usersInfo?.filter((user) => user.signingStatus === SigningStatus.SIGNED);
    return signed?.length ?? 0;
  });
  const approvedList = parties.filter(
    (party) => party.snapshotStatus?.approvalStatus === ApprovalStatus.ACCEPT,
  );
  const numberOfApproved = sumBy(approvedList, (item) => item.usersInfo?.length ?? 0);

  contractManager.partyName = currentParty?.displayName ?? '';
  contractManager.theOtherParty = theOtherParty ?? null;
  contractManager.theOtherClaimReviewParty = theOtherClaimReviewParty ?? null;

  contractManager.payeeParty = payeeParty;
  contractManager.payerParty = payerParty;
  contractManager.inviteeParty = inviteeParty;
  contractManager.inviterParty = inviterParty;

  contractManager.architectParty = architect;
  contractManager.architectName = architect?.displayName ?? '';

  contractManager.superintendentParty = superintendent;
  contractManager.superintendentPartyName = superintendent?.displayName ?? '';

  contractManager.payerAgentParty = payerAgent;

  contractManager.funders = funders;
  contractManager.qss = qss;

  contractManager.isPayee = isPayee;
  contractManager.isPayer = isPayer;
  contractManager.isInvitee = isInvitee;
  contractManager.isInviter = isInviter;
  contractManager.isArchitect = isArchitect;
  contractManager.isSuperintendent = isSuperintendent;
  contractManager.isFunder = isFunder;
  contractManager.isQS = isQS;
  contractManager.isNonContractualParty = isNonContractualParty;
  contractManager.isPayerHasAccount = isPayerHasAccount;
  contractManager.participantParties = parties;
  contractManager.numberOfParticipants = numberOfParticipants;
  contractManager.numberOfJoined = numberOfJoined;
  contractManager.allContractualPartiesJoined = allContractualPartiesJoined;
  contractManager.numberOfSigned = numberOfSigned;
  contractManager.numberOfSignatories = numberOfSignatories;
  contractManager.numberOfApproved = numberOfApproved;

  contractManager.currentParty = currentParty;
  contractManager.lastReviewParty = lastReviewParty;
  // set current party account ready status
  saveProjectUserGroupAccount(currentParty?.account?.id);
};
